import React, { useState, useEffect } from 'react';
import { View, StyleSheet, FlatList, Image, Dimensions, useWindowDimensions } from 'react-native';
import { Card, Title, Paragraph, Button, FAB, Dialog, Portal, TextInput, Snackbar, ActivityIndicator, useTheme, IconButton, Divider, Chip } from 'react-native-paper';
import { supabase } from '../../services/supabase';
import { evolutionApiService, QRCodeResponse } from '../../services/evolutionApiService';

type Props = {
  navigation: any;
};

type InstanciaWhatsApp = {
  id?: number;
  rotaapi: string;
  codempresa: number;
  tipo_integracao: string;
  apikey_instance: string;
  instance_name: string;
  numero_whatsapp: string;
  api_whatsapp?: any; // Dados da API incluindo status
};

type StatusInstancia = {
  instanceName: string;
  status: string; // 'open', 'close', 'connecting', etc
};

export function GerenciarInstanciasWhatsApp({ navigation }: Props) {
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const [loading, setLoading] = useState(false);
  const [instancias, setInstancias] = useState<InstanciaWhatsApp[]>([]);
  const [empresas, setEmpresas] = useState<{id: number, nome: string}[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [currentInstancia, setCurrentInstancia] = useState<InstanciaWhatsApp>({
    rotaapi: '',
    codempresa: 0,
    tipo_integracao: 'smartcobranca',
    apikey_instance: '',
    instance_name: '',
    numero_whatsapp: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [userEmpresa, setUserEmpresa] = useState<number | null>(null);
  const [qrCodeDialogVisible, setQRCodeDialogVisible] = useState(false);
  const [qrCodeData, setQRCodeData] = useState<QRCodeResponse | null>(null);
  const [loadingQRCode, setLoadingQRCode] = useState(false);
  const [currentInstanceName, setCurrentInstanceName] = useState('');
  const [statusInstancias, setStatusInstancias] = useState<Record<string, string>>({});

  const loadInstancias = React.useCallback(async () => {
    try {
      setLoading(true);
      
      // Limpar cache da API Evolution antes de buscar
      evolutionApiService.limparCache();
      
      // Obter o usuário atual e sua empresa
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('admin, empresa')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      
      // Armazenar a empresa do usuário
      const empresaId = userData?.empresa;
      setUserEmpresa(empresaId);
      
      if (!empresaId) {
        throw new Error('Usuário não está associado a nenhuma empresa');
      }

      // Buscar todas as instâncias da empresa do usuário (sem filtro de tipo)
      const { data, error } = await supabase
        .from('infraestrutura_chatbot')
        .select('*')
        .eq('codempresa', empresaId);

      if (error) {
        console.error('Erro ao buscar instâncias:', error);
        throw error;
      }
      
      console.log(`Instâncias carregadas: ${data?.length || 0}`);
      setInstancias(data || []);
      
      // Buscar status de cada instância
      if (data && data.length > 0) {
        await buscarStatusInstancias(data);
      }
    } catch (err: any) {
      console.error('Erro completo:', err);
      setError(err.message || 'Erro ao carregar instâncias');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Efeito para carregar dados iniciais
  useEffect(() => {
    loadInstancias();
    loadEmpresas();
  }, [loadInstancias]);

  const loadEmpresas = async () => {
    try {
      // Obter o usuário atual e sua empresa
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('empresa')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      
      // Buscar apenas a empresa do usuário
      if (userData?.empresa) {
        const { data, error } = await supabase
          .from('empresa')
          .select('id, nome, rotaapi')
          .eq('id', userData.empresa)
          .single();
  
        if (error) throw error;
        
        setEmpresas(data ? [data] : []);
        
        // Se encontrou a empresa e ela tem rotaapi definida, usar como valor padrão
        if (data && data.rotaapi) {
          setCurrentInstancia(prev => ({
            ...prev,
            rotaapi: data.rotaapi
          }));
        }
      }
    } catch (err: any) {
      console.error('Erro ao carregar empresa:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if (!currentInstancia.numero_whatsapp || !currentInstancia.instance_name) {
        setError('Preencha o número do WhatsApp e o nome da instância');
        setLoading(false);
        return;
      }

      // Obter o ID da empresa do usuário atual
      if (!userEmpresa) {
        throw new Error('Empresa do usuário não identificada');
      }

      // Buscar a rotaapi da empresa do usuário
      const { data: empresaData, error: empresaError } = await supabase
        .from('empresa')
        .select('rotaapi')
        .eq('id', userEmpresa)
        .single();

      if (empresaError || !empresaData?.rotaapi) {
        throw new Error('Não foi possível obter a rota API da empresa');
      }

      // Preparar objeto para salvar
      const instanciaParaSalvar = {
        rotaapi: empresaData.rotaapi, // Sempre usa a rotaapi da empresa
        codempresa: userEmpresa,
        tipo_integracao: 'smartcobranca', // Fixo para usuários comuns
        apikey_instance: currentInstancia.apikey_instance,
        instance_name: currentInstancia.instance_name,
        numero_whatsapp: currentInstancia.numero_whatsapp
      };

      let result;

      if (isEditing && currentInstancia.id) {
        // Verificar se a instância pertence à empresa do usuário
        const { data: instanciaAtual, error: checkError } = await supabase
          .from('infraestrutura_chatbot')
          .select('*')
          .eq('id', currentInstancia.id)
          .eq('codempresa', userEmpresa)
          .single();
          
        if (checkError || !instanciaAtual) {
          throw new Error('Você não tem permissão para editar esta instância');
        }
        
        // Atualizar instância existente
        result = await supabase
          .from('infraestrutura_chatbot')
          .update(instanciaParaSalvar)
          .eq('id', currentInstancia.id);

        if (result.error) {
          console.error('Erro ao atualizar instância:', result.error);
          throw result.error;
        }
        
        setSuccess('Instância atualizada com sucesso!');
        console.log('Instância atualizada com sucesso!');
      } else {
        // Criar nova instância
        result = await supabase
          .from('infraestrutura_chatbot')
          .insert(instanciaParaSalvar);

        if (result.error) {
          console.error('Erro ao criar instância:', result.error);
          throw result.error;
        }
        
        setSuccess('Instância criada com sucesso!');
        console.log('Instância criada com sucesso!');
      }

      setDialogVisible(false);
      loadInstancias();
    } catch (err: any) {
      console.error('Erro completo ao salvar:', err);
      setError(err.message || 'Erro ao salvar instância');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      
      if (!currentInstancia.id) {
        setError('ID da instância não encontrado');
        setLoading(false);
        return;
      }
      
      // Verificar se a instância pertence à empresa do usuário
      const { data: instanciaAtual, error: checkError } = await supabase
        .from('infraestrutura_chatbot')
        .select('*')
        .eq('id', currentInstancia.id)
        .eq('codempresa', userEmpresa)
        .single();
        
      if (checkError || !instanciaAtual) {
        throw new Error('Você não tem permissão para excluir esta instância');
      }

      const { error } = await supabase
        .from('infraestrutura_chatbot')
        .delete()
        .eq('id', currentInstancia.id);

      if (error) throw error;
      
      setSuccess('Instância excluída com sucesso!');
      setDeleteDialogVisible(false);
      loadInstancias();
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir instância');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setCurrentInstancia({
      rotaapi: '',
      codempresa: userEmpresa || 0,
      tipo_integracao: 'smartcobranca',
      apikey_instance: '',
      instance_name: '',
      numero_whatsapp: ''
    });
    setIsEditing(false);
    setDialogVisible(true);
  };

  const openEditDialog = (instancia: InstanciaWhatsApp) => {
    // Extrair número do owner se disponível
    let numeroExtraido = instancia.numero_whatsapp || '';
    if (instancia.api_whatsapp?.owner) {
      const numeroMatch = instancia.api_whatsapp.owner.match(/^(\d+)@/);
      if (numeroMatch) {
        numeroExtraido = numeroMatch[1];
      }
    }
    
    setCurrentInstancia({
      ...instancia,
      numero_whatsapp: numeroExtraido
    });
    setIsEditing(true);
    setDialogVisible(true);
  };

  const openDeleteDialog = (instancia: InstanciaWhatsApp) => {
    setCurrentInstancia(instancia);
    setDeleteDialogVisible(true);
  };

  const buscarStatusInstancias = async (instancias: InstanciaWhatsApp[]) => {
    try {
      const statusMap: Record<string, string> = {};
      const instanciasAtualizadas: InstanciaWhatsApp[] = [];
      
      console.log('\n=== INÍCIO: Buscar status das instâncias ===');
      console.log(`Total de instâncias do usuário no banco: ${instancias.length}`);
      console.log('Instâncias a serem buscadas:', instancias.map(i => `${i.instance_name} (${i.tipo_integracao})`).join(', '));
      
      // Buscar detalhes de cada instância usando o parâmetro instanceName
      const detalhesPromises = instancias.map(async (inst) => {
        try {
          console.log(`\n[${inst.tipo_integracao}] Buscando: ${inst.instance_name}`);
          
          // Buscar instância específica com parâmetro instanceName
          const detalhes = await evolutionApiService.buscarDetalhesInstancia(inst.instance_name);
          
          if (detalhes && detalhes.notFound) {
            // Instância não existe na API Evolution (404)
            console.log(`⚠️ Instância ${inst.instance_name} NÃO CADASTRADA na API Evolution`);
            console.log(`  - Tipo: ${inst.tipo_integracao}`);
            console.log(`  - Status: not_registered`);
            
            statusMap[inst.instance_name] = 'not_registered';
            instanciasAtualizadas.push({
              ...inst,
              api_whatsapp: {
                ...inst.api_whatsapp,
                status: 'not_registered'
              }
            });
          } else if (detalhes) {
            console.log(`✓ Instância ${inst.instance_name} encontrada na API Evolution`);
            console.log(`  - Tipo: ${inst.tipo_integracao}`);
            console.log(`  - Status: ${detalhes.connectionStatus || detalhes.state}`);
            console.log(`  - Perfil: ${detalhes.profileName}`);
            console.log(`  - Número: ${detalhes.ownerJid}`);
            
            // Atualizar status
            statusMap[inst.instance_name] = detalhes.connectionStatus || detalhes.state || 'close';
            
            // Atualizar dados da instância com informações da API
            const instanciaAtualizada = {
              ...inst,
              api_whatsapp: {
                ...inst.api_whatsapp,
                profileName: detalhes.profileName || inst.api_whatsapp?.profileName,
                profilePictureUrl: detalhes.profilePicUrl || detalhes.profilePictureUrl || inst.api_whatsapp?.profilePictureUrl,
                status: detalhes.connectionStatus || detalhes.state,
                owner: detalhes.ownerJid || inst.api_whatsapp?.owner
              }
            };
            instanciasAtualizadas.push(instanciaAtualizada);
          } else {
            console.log(`✗ Erro ao buscar instância ${inst.instance_name}`);
            console.log(`  - Tipo: ${inst.tipo_integracao}`);
            console.log(`  - Usando dados do banco de dados`);
            
            // Usar dados do banco se não encontrar na API
            if (inst.api_whatsapp?.status) {
              statusMap[inst.instance_name] = inst.api_whatsapp.status;
            } else {
              statusMap[inst.instance_name] = 'close';
            }
            instanciasAtualizadas.push(inst);
          }
        } catch (err) {
          console.error(`✗ Erro ao buscar instância ${inst.instance_name}:`, err);
          statusMap[inst.instance_name] = 'unknown';
          instanciasAtualizadas.push(inst);
        }
      });
      
      await Promise.all(detalhesPromises);
      
      console.log('\n=== RESULTADO ===');
      console.log(`Instâncias processadas: ${instanciasAtualizadas.length}`);
      console.log('Breakdown por tipo:');
      const tipoCount: Record<string, number> = {};
      instanciasAtualizadas.forEach(inst => {
        tipoCount[inst.tipo_integracao] = (tipoCount[inst.tipo_integracao] || 0) + 1;
      });
      Object.entries(tipoCount).forEach(([tipo, count]) => {
        console.log(`  - ${tipo}: ${count} instância(s)`);
      });
      console.log('\nStatus das instâncias:', statusMap);
      console.log('=== FIM ===\n');
      
      setStatusInstancias(statusMap);
      
      // Atualizar instâncias com novos dados
      if (instanciasAtualizadas.length > 0) {
        setInstancias(instanciasAtualizadas);
      }
    } catch (err) {
      console.error('Erro ao buscar status das instâncias:', err);
    }
  };

  const handleGetQRCode = async (instanceName: string) => {
    try {
      setLoadingQRCode(true);
      setCurrentInstanceName(instanceName);
      setQRCodeDialogVisible(true);
      
      const response = await evolutionApiService.obterQRCode(instanceName);
      
      if (!response) {
        setError('Erro ao obter QR code. Verifique se a instância existe.');
        setQRCodeDialogVisible(false);
        return;
      }
      
      setQRCodeData(response);
      setSuccess('QR code obtido com sucesso');
    } catch (error: any) {
      console.error('Erro ao obter QR code:', error);
      setError(`Erro: ${error.message || 'Falha ao obter QR code'}`);
      setQRCodeDialogVisible(false);
    } finally {
      setLoadingQRCode(false);
    }
  };

  // Calcular número de colunas baseado na largura
  const getNumColumns = () => {
    if (width >= 1200) return 3; // Desktop grande: 3 colunas
    if (width >= 768) return 2;  // Tablet: 2 colunas
    return 1;                     // Mobile: 1 coluna
  };

  const numColumns = getNumColumns();

  const renderItem = ({ item }: { item: InstanciaWhatsApp }) => {
    const empresa = empresas.find(e => e.id === item.codempresa);
    const status = statusInstancias[item.instance_name] || 'unknown';
    const isConnected = status === 'open';
    
    // Obter dados do perfil da API WhatsApp
    const profileName = item.api_whatsapp?.profileName || item.instance_name;
    const profilePictureUrl = item.api_whatsapp?.profilePictureUrl;
    
    // Formatar número: pegar do api_whatsapp.owner ou numero_whatsapp
    let numeroFormatado = 'Não informado';
    if (item.api_whatsapp?.owner) {
      // Extrair número do formato "5582999999999@s.whatsapp.net"
      const numeroMatch = item.api_whatsapp.owner.match(/^(\d+)@/);
      if (numeroMatch) {
        numeroFormatado = numeroMatch[1];
      }
    } else if (item.numero_whatsapp) {
      numeroFormatado = item.numero_whatsapp;
    }
    
    console.log('Renderizando instância:', item.instance_name);
    console.log('Profile Picture URL:', profilePictureUrl);
    console.log('Número formatado:', numeroFormatado);
    
    return (
      <Card style={styles.modernCard} elevation={2}>
        <Card.Content style={styles.modernCardContent}>
          {/* Cabeçalho com nome da instância */}
          <View style={styles.modernHeader}>
            <Title style={styles.instanceTitle}>{item.instance_name}</Title>
            <IconButton
              icon="cog"
              size={20}
              onPress={() => openEditDialog(item)}
              iconColor="#666"
              style={styles.settingsIcon}
            />
          </View>
          
          <Divider style={styles.modernDivider} />
          
          {/* Perfil com foto, nome e número */}
          <View style={styles.profileSection}>
            <View style={styles.avatarContainer}>
              {profilePictureUrl ? (
                <Image
                  source={{ uri: profilePictureUrl }}
                  style={styles.avatar}
                  onError={(e) => console.log('Erro ao carregar imagem:', e.nativeEvent.error)}
                />
              ) : (
                <View style={styles.avatarPlaceholder}>
                  <IconButton icon="account" size={20} iconColor="#999" style={{margin: 0}} />
                </View>
              )}
            </View>
            
            <View style={styles.profileInfo}>
              <Title style={styles.profileName}>{profileName}</Title>
              <Paragraph style={styles.profileNumber}>{numeroFormatado}</Paragraph>
            </View>
          </View>
          
          {/* Status e Ações */}
          <View style={styles.actionsSection}>
            {status === 'not_registered' ? (
              <View style={styles.notRegisteredBadge}>
                <IconButton icon="alert-circle" size={16} iconColor="#fff" style={styles.badgeIcon} />
                <Paragraph style={styles.notRegisteredText}>Não Cadastrada</Paragraph>
              </View>
            ) : isConnected ? (
              <View style={styles.connectedBadge}>
                <IconButton icon="check-circle" size={16} iconColor="#fff" style={styles.badgeIcon} />
                <Paragraph style={styles.connectedText}>Conectado</Paragraph>
              </View>
            ) : (
              <Button
                mode="contained"
                icon="qrcode"
                onPress={() => handleGetQRCode(item.instance_name)}
                style={styles.connectButton}
                labelStyle={styles.connectButtonLabel}
              >
                Conectar
              </Button>
            )}
          </View>
        </Card.Content>
      </Card>
    );
  };

  if (loading && instancias.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Title style={styles.title}>Gerenciar Instâncias do WhatsApp</Title>
      <Paragraph style={styles.subtitle}>Configure as instâncias de WhatsApp para sua empresa</Paragraph>

      {instancias.length === 0 ? (
        <Card style={styles.emptyCard}>
          <Card.Content>
            <Title style={styles.emptyTitle}>Nenhuma instância cadastrada</Title>
            <Paragraph style={styles.emptyText}>Clique no botão + para adicionar uma nova instância.</Paragraph>
          </Card.Content>
        </Card>
      ) : (
        <FlatList
          data={instancias}
          renderItem={renderItem}
          keyExtractor={(item) => item.id?.toString() || Math.random().toString()}
          contentContainerStyle={styles.listContainer}
          key={numColumns}
          numColumns={numColumns}
          columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : undefined}
        />
      )}

      <FAB
        style={styles.fab}
        icon="plus"
        onPress={openAddDialog}
      />

      <Portal>
        {/* Dialog para adicionar/editar instância */}
        <Dialog 
          visible={dialogVisible} 
          onDismiss={() => setDialogVisible(false)}
          style={styles.editDialog}
        >
          <Dialog.Title style={styles.dialogTitle}>
            <IconButton icon={isEditing ? "pencil" : "plus-circle"} size={24} iconColor="#25D366" />
            {isEditing ? 'Editar Instância' : 'Nova Instância'}
          </Dialog.Title>
          <Dialog.Content style={styles.dialogContent}>
            <View style={styles.inputContainer}>
              <TextInput
                label="Número do WhatsApp"
                value={currentInstancia.numero_whatsapp}
                onChangeText={(text) => setCurrentInstancia(prev => ({ ...prev, numero_whatsapp: text }))}
                mode="outlined"
                style={styles.input}
                placeholder="Ex: 5582999999999"
                keyboardType="phone-pad"
                outlineColor="#e0e0e0"
                activeOutlineColor="#25D366"
                left={<TextInput.Icon icon="phone" color="#25D366" />}
              />
            </View>
            
            <View style={styles.inputContainer}>
              <TextInput
                label="Nome da Instância"
                value={currentInstancia.instance_name}
                onChangeText={(text) => setCurrentInstancia(prev => ({ ...prev, instance_name: text }))}
                mode="outlined"
                style={styles.input}
                placeholder="Ex: ML-COBRANCA"
                outlineColor="#e0e0e0"
                activeOutlineColor="#25D366"
                left={<TextInput.Icon icon="tag" color="#25D366" />}
                disabled={isEditing}
              />
            </View>
            
            {isEditing && (
              <View style={styles.infoBox}>
                <IconButton icon="information" size={20} iconColor="#2196F3" style={styles.infoBoxIcon} />
                <Paragraph style={styles.infoBoxText}>
                  O nome da instância não pode ser alterado após a criação.
                </Paragraph>
              </View>
            )}
          </Dialog.Content>
          <Dialog.Actions style={styles.dialogActions}>
            <Button 
              onPress={() => setDialogVisible(false)}
              textColor="#666"
              style={styles.cancelButton}
            >
              Cancelar
            </Button>
            <Button 
              onPress={handleSave} 
              loading={loading}
              mode="contained"
              buttonColor="#25D366"
              style={styles.saveButton}
              icon="check"
            >
              Salvar
            </Button>
          </Dialog.Actions>
        </Dialog>

        {/* Dialog para confirmar exclusão */}
        <Dialog visible={deleteDialogVisible} onDismiss={() => setDeleteDialogVisible(false)}>
          <Dialog.Title>Confirmar Exclusão</Dialog.Title>
          <Dialog.Content>
            <Paragraph>Tem certeza que deseja excluir a instância "{currentInstancia.instance_name}"?</Paragraph>
            <Paragraph style={styles.warningText}>Esta ação não pode ser desfeita.</Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteDialogVisible(false)}>Cancelar</Button>
            <Button onPress={handleDelete} color="#F44336" loading={loading}>Excluir</Button>
          </Dialog.Actions>
        </Dialog>

        <Snackbar
          visible={!!error}
          onDismiss={() => setError(null)}
          duration={3000}
          style={{ backgroundColor: '#F44336' }}
        >
          {error}
        </Snackbar>

        <Snackbar
          visible={!!success}
          onDismiss={() => setSuccess(null)}
          duration={3000}
          style={{ backgroundColor: '#4CAF50' }}
        >
          {success}
        </Snackbar>

        {/* Dialog para exibir QR code */}
        <Dialog 
          visible={qrCodeDialogVisible} 
          onDismiss={() => {
            setQRCodeDialogVisible(false);
            setQRCodeData(null);
          }} 
          style={styles.qrCodeDialog}
        >
          <Dialog.Title style={styles.qrDialogTitle}>Conectar WhatsApp</Dialog.Title>
          <Dialog.Content>
            {loadingQRCode ? (
              <View style={styles.qrCodeLoading}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Paragraph style={styles.qrCodeLoadingText}>Obtendo QR code...</Paragraph>
              </View>
            ) : qrCodeData ? (
              <View style={styles.qrCodeContainer}>
                <Chip 
                  icon="information" 
                  mode="outlined"
                  style={styles.instanceChip}
                >
                  {currentInstanceName}
                </Chip>
                
                <View style={styles.qrCodeImageContainer}>
                  <Image
                    source={{ uri: qrCodeData.base64 }}
                    style={styles.qrCodeImage}
                    resizeMode="contain"
                  />
                </View>
                
                <View style={styles.instructionsContainer}>
                  <Paragraph style={styles.qrCodeInstructions}>
                    📱 <Paragraph style={styles.boldText}>Como conectar:</Paragraph>
                  </Paragraph>
                  <Paragraph style={styles.instructionStep}>
                    1. Abra o WhatsApp no seu celular
                  </Paragraph>
                  <Paragraph style={styles.instructionStep}>
                    2. Vá em <Paragraph style={styles.boldText}>Menu → Dispositivos Conectados</Paragraph>
                  </Paragraph>
                  <Paragraph style={styles.instructionStep}>
                    3. Toque em <Paragraph style={styles.boldText}>Conectar um dispositivo</Paragraph>
                  </Paragraph>
                  <Paragraph style={styles.instructionStep}>
                    4. Escaneie este QR code
                  </Paragraph>
                </View>
              </View>
            ) : (
              <Paragraph style={styles.errorText}>Não foi possível obter o QR code.</Paragraph>
            )}
          </Dialog.Content>
          <Dialog.Actions>
            {qrCodeData && (
              <Button 
                onPress={() => handleGetQRCode(currentInstanceName)} 
                loading={loadingQRCode} 
                disabled={loadingQRCode}
                icon="refresh"
              >
                Atualizar
              </Button>
            )}
            <Button onPress={() => {
              setQRCodeDialogVisible(false);
              setQRCodeData(null);
            }}>
              Fechar
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
    color: '#1a1a1a',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
    color: '#666',
  },
  listContainer: {
    paddingBottom: 80,
  },
  columnWrapper: {
    justifyContent: 'flex-start',
    gap: 8,
  },
  card: {
    marginBottom: 16,
    backgroundColor: '#fff',
    borderRadius: 12,
  },
  modernCard: {
    marginBottom: 8,
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    flex: 1,
    maxWidth: 400,
  },
  modernCardContent: {
    padding: 12,
  },
  modernHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  instanceTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1a1a1a',
    letterSpacing: 0.2,
  },
  settingsIcon: {
    margin: 0,
  },
  modernDivider: {
    backgroundColor: '#e0e0e0',
    marginVertical: 6,
  },
  profileSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  avatarContainer: {
    marginRight: 10,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e0e0e0',
  },
  avatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e0e0e0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 1,
  },
  profileNumber: {
    fontSize: 12,
    color: '#666',
  },
  actionsSection: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  connectedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 16,
    flex: 1,
  },
  badgeIcon: {
    margin: 0,
    marginRight: -8,
  },
  connectedText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 12,
  },
  notRegisteredBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f59e0b',
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 16,
    flex: 1,
  },
  notRegisteredText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 12,
  },
  connectButton: {
    flex: 1,
    backgroundColor: '#25D366',
    borderRadius: 16,
    paddingVertical: 0,
  },
  connectButtonLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  cardHeader: {
    marginBottom: 8,
  },
  cardTitleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
  },
  statusChip: {
    backgroundColor: '#25D366',
  },
  chipText: {
    color: '#fff',
    fontSize: 12,
  },
  divider: {
    marginVertical: 12,
  },
  infoContainer: {
    marginBottom: 8,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  infoIcon: {
    margin: 0,
    marginRight: -8,
  },
  infoText: {
    fontSize: 14,
    color: '#666',
    flex: 1,
  },
  cardActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
  },
  qrButton: {
    flex: 1,
    marginRight: 8,
    backgroundColor: '#25D366',
  },
  connectedButton: {
    flex: 1,
    marginRight: 8,
    borderColor: '#4CAF50',
  },
  buttonLabel: {
    fontSize: 14,
  },
  iconActions: {
    flexDirection: 'row',
  },
  emptyCard: {
    marginTop: 20,
    backgroundColor: '#fff',
  },
  emptyTitle: {
    color: '#1a1a1a',
    textAlign: 'center',
  },
  emptyText: {
    color: '#666',
    textAlign: 'center',
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
    backgroundColor: '#25D366',
  },
  input: {
    marginBottom: 12,
  },
  warningText: {
    color: '#F44336',
    marginTop: 8,
  },
  editDialog: {
    maxWidth: 500,
    alignSelf: 'center',
    backgroundColor: '#fff',
  },
  dialogTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1a1a1a',
  },
  dialogContent: {
    paddingTop: 20,
  },
  inputContainer: {
    marginBottom: 16,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E3F2FD',
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  infoBoxIcon: {
    margin: 0,
    marginRight: -8,
  },
  infoBoxText: {
    flex: 1,
    fontSize: 13,
    color: '#1976D2',
  },
  dialogActions: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  cancelButton: {
    marginRight: 8,
  },
  saveButton: {
    paddingHorizontal: 16,
  },
  qrCodeDialog: {
    maxWidth: 500,
    alignSelf: 'center',
  },
  qrDialogTitle: {
    textAlign: 'center',
    fontSize: 20,
    fontWeight: 'bold',
  },
  qrCodeLoading: {
    padding: 20,
    alignItems: 'center',
  },
  qrCodeLoadingText: {
    marginTop: 12,
    color: '#666',
  },
  qrCodeContainer: {
    alignItems: 'center',
  },
  instanceChip: {
    marginBottom: 16,
  },
  qrCodeImageContainer: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 12,
    elevation: 2,
    marginBottom: 16,
  },
  qrCodeImage: {
    width: 250,
    height: 250,
  },
  instructionsContainer: {
    backgroundColor: '#f5f5f5',
    padding: 16,
    borderRadius: 8,
    width: '100%',
  },
  qrCodeInstructions: {
    fontSize: 16,
    marginBottom: 12,
    fontWeight: 'bold',
  },
  instructionStep: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
    paddingLeft: 8,
  },
  boldText: {
    fontWeight: 'bold',
    color: '#000',
  },
  errorText: {
    color: '#F44336',
    textAlign: 'center',
    padding: 20,
  },
});
